import React from 'react';
import { listaDeNombres } from '../utils/captura';
import { Chip } from './ui/Chip';
import { cx } from './ui/clases';

export type FiltroColaboradores = 'todos' | 'pendientes';

interface ChipsProps {
  filtro: FiltroColaboradores;
  onFiltro: (f: FiltroColaboradores) => void;
  total: number;
  pendientes: number;
  /** `compacto`: 32 px, dentro del banner (escritorio). */
  compacto?: boolean;
  className?: string;
}

/**
 * «Todos · 7» / «Por resolver · 2». Un grupo de botones con `aria-pressed`
 * (SPEC §8.5); «Por resolver» se apaga cuando no hay nada por resolver.
 */
export const ChipsColaboradores: React.FC<ChipsProps> = ({ filtro, onFiltro, total, pendientes, compacto, className }) => (
  <div role="group" aria-label="Filtrar colaboradores" className={cx('flex items-center gap-2', className)}>
    <Chip activo={filtro === 'todos'} onClick={() => onFiltro('todos')} contador={total} tamano={compacto ? 'compacto' : 'normal'}>
      Todos
    </Chip>
    <Chip
      activo={filtro === 'pendientes'}
      onClick={() => onFiltro('pendientes')}
      contador={pendientes}
      disabled={pendientes === 0 && filtro !== 'pendientes'}
      tamano={compacto ? 'compacto' : 'normal'}
    >
      Por resolver
    </Chip>
  </div>
);

interface Props extends Omit<ChipsProps, 'compacto' | 'className'> {
  /** Nombres de quienes no tienen meta ni motivo (en el orden de la tabla). */
  nombres: string[];
}

const IconoAviso = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-metas-aviso-icono">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v6M12 16.5h.01" />
  </svg>
);

/**
 * El banner ámbar de arriba de la tabla (diseño `Main`): «Por resolver antes
 * de enviar: Rosa Elena Martínez Cruz y Luis Fernando Ramírez Soto no tienen
 * meta ni motivo.», con los chips para ver sólo a esas personas. Con muchas,
 * los primeros tres y «y N más»: la lista completa está en la tabla filtrada.
 */
export const BannerPorResolver: React.FC<Props> = ({ nombres, filtro, onFiltro, total, pendientes }) => {
  // Con el filtro puesto y ya todo resuelto, el banner se queda (sin nombres)
  // para poder volver a «Todos».
  if (pendientes === 0 && filtro !== 'pendientes') return null;
  const quien = listaDeNombres(nombres);
  const verbo = nombres.length === 1 ? 'no tiene' : 'no tienen';
  return (
    <div
      className="flex flex-wrap items-center gap-3 border-b border-metas-banner-borde bg-metas-banner px-4 py-3"
      data-testid="banner-por-resolver"
    >
      <IconoAviso />
      <p className="m-0 min-w-0 flex-1 text-sm text-metas-banner-texto">
        {pendientes > 0 ? (
          <>
            <b className="font-semibold">Por resolver antes de enviar:</b> {quien} {verbo} meta ni motivo.
          </>
        ) : (
          <b className="font-semibold">Ya no queda nadie por resolver.</b>
        )}
      </p>
      <div className="flex items-center gap-1">
        <span className="mr-1 text-[13px] text-metas-banner-texto">Mostrar</span>
        <ChipsColaboradores filtro={filtro} onFiltro={onFiltro} total={total} pendientes={pendientes} compacto />
      </div>
    </div>
  );
};

export default BannerPorResolver;
